/** Until insights uses mp-line-chart any changes in here will also
 * need to be made in iron/apps/insights/src/insights-app/insights-result/chart-display/line-chart/mp-line-chart/mixins/annotations.js */
import moment from 'moment-timezone';
import sortedLastIndex from 'lodash/sortedLastIndex';
import type {Annotation as HigchartsAnnotation} from 'highcharts';

import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {annotationsClient} from 'common/report/queries/app-api/annotations-client';
import {offsetForHighcharts} from 'common/util/date';
import {formatDate, MS_BY_UNIT} from 'common/util/date';

import {Annotation, AnnotationState} from '../types';
import type {LineChart} from '../index';
import {AnnotationEvent, ButtonLocationDetails, HighChartXAxis} from './type';
import {track, TrackingEvent, TrackingEventProperty} from './tracking';

export const EXISTING_ANNOTATIONS_BUTTON_Y = -4;
export const ANNOTATION_PLOT_LINE_ID = `annotation-plot-line`;
export const HOVER_PLOT_LINE_ID = `hover-plot-line`;
export const CREATE_ANNOTATION_ID = `create-annotation-id`;
export const ANNOTATION_GROUP_BUTTON_ID_PREFIX = `annotation-group-button`;

export class Annotations {
  private ticks: string[];

  constructor(public lineChart: LineChart) {}

  static getHelpers(lineChart: LineChart) {
    return {
      add: ({detail: annotation}: CustomEvent<AnnotationEvent>) => {
        lineChart.annotations.process([annotation.annotation]);
        lineChart.annotations.closePopup();
      },
      close: () => {
        lineChart.annotations.closePopup();
      },
      delete: ({detail: annotation}: CustomEvent) =>
        lineChart.annotations.updateLineChartState({deletingAnnotation: annotation}),
      deleteCanceled: () => lineChart.annotations.updateLineChartState({deletingAnnotation: null}),
      deleteConfirmed: async () => {
        const projectId = parseInt(getProjectId());
        const workspaceId = parseInt(getWorkspaceId());
        const timeIndex = lineChart.annotations.lineChart.state.annotations.activeAnnotationButton.timestampMs;
        const {deletingAnnotation} = lineChart.annotations.lineChart.state.annotations;

        await annotationsClient.deleteAnnotation({projectId, id: deletingAnnotation.id, workspaceId});

        const annotationRemaining = lineChart.annotations.lineChart.state.annotations.allAnnotations
          .get(timeIndex)
          .filter((annotation) => annotation.id !== deletingAnnotation.id);

        const allAnnotations = lineChart.annotations.lineChart.state.annotations.allAnnotations;

        if (annotationRemaining.length === 0) {
          allAnnotations.delete(timeIndex);
        } else {
          allAnnotations.set(timeIndex, annotationRemaining);
        }

        lineChart.annotations.updateLineChartState({allAnnotations});

        lineChart.annotations.setAnnotations();

        if (annotationRemaining.length === 0) {
          lineChart.annotations.closePopup();
        }

        lineChart.annotations.updateLineChartState({deletingAnnotation: null});

        track(TrackingEvent.Delete, {
          [TrackingEventProperty.AnnotationText]: deletingAnnotation.description,
          [TrackingEventProperty.AnnotationTimestamp]: moment(deletingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
          [TrackingEventProperty.ReportName]: getReportName(),
        });
      },
      edit: ({detail: {editingAnnotation, annotationDescriptionDraft}}: CustomEvent) => {
        lineChart.annotations.updateLineChartState({editingAnnotation, annotationDescriptionDraft});
      },
      editCanceled: () => lineChart.annotations.updateLineChartState({editingAnnotation: null}),
      popupChange: (ev) => {
        if (ev.detail && ev.detail.state === `closed`) {
          lineChart.annotations.popupClosed();
        }
      },
      save: async () => {
        const {editingAnnotation} = lineChart.annotations.lineChart.state.annotations;
        if (editingAnnotation) {
          const {allAnnotations, annotationDescriptionDraft} = lineChart.annotations.lineChart.state.annotations;
          const projectId = parseInt(getProjectId());
          const workspaceId = parseInt(getWorkspaceId());
          const {id} = editingAnnotation;
          await annotationsClient.updateAnnotation({
            projectId,
            id,
            description: annotationDescriptionDraft,
            workspaceId,
          });
          editingAnnotation.description = annotationDescriptionDraft;

          // Update the annotation that we've saved to show immediately
          allAnnotations.forEach((value) =>
            value.forEach((entry) => {
              if (entry.id === id) {
                entry.description = annotationDescriptionDraft;
              }
            }),
          );

          lineChart.annotations.updateLineChartState({editingAnnotation: null, allAnnotations});
          lineChart.annotations.setAnnotations();

          track(TrackingEvent.Edit, {
            [TrackingEventProperty.AnnotationText]: editingAnnotation.description,
            [TrackingEventProperty.AnnotationTimestamp]: moment(editingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
            [TrackingEventProperty.ReportName]: getReportName(),
          });
        }
      },
      showCreate: () =>
        lineChart.annotations.showCreateAnnotation(
          lineChart.annotations.lineChart.state.annotations.activeAnnotationButton,
        ),
      updateDescriptionDraft: ({detail}: CustomEvent) =>
        lineChart.annotations.updateLineChartState({annotationDescriptionDraft: detail}),
    };
  }

  getDefaultState(): any {
    return {
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
      createAnnotationDate: ``,
      visibleAnnotations: new Map<number, any>(),
      allAnnotations: new Map<number, any>(),
      activeAnnotationButtonTimestamp: null,
      editingAnnotation: null,
      viewingAnnotations: false,
      activeAnnotationPreview: null,
      annotationDescriptionDraft: null,
      deletingAnnotation: null,
    };
  }

  closePopup() {
    this.updateLineChartState({activeAnnotationPopupClosing: true});
  }

  areLoaded() {
    return Boolean(this.ticks);
  }

  getXAxisMinAndMax(): HighChartXAxis {
    const {dataMax, dataMin} = this.lineChart.getHighcharts().xAxis[0].getExtremes();
    const maxDateMs = dataMax + MS_BY_UNIT[this.lineChart.attr(`time-unit`)];
    return {minDateMs: dataMin, maxDateMs};
  }

  async load() {
    // we rely on timestamps generated from the series so we don't need to deal with timezones and whatnot
    const firstSeries = this.lineChart.getHighcharts().series[0];
    if (!firstSeries) {
      return;
    }

    // @ts-ignore Property 'processedXData' does not exist on type 'Series'.
    this.ticks = firstSeries.processedXData.map((num) => num.toString());
    const xAxis = this.getXAxisMinAndMax();
    const formatDate = (timestamp) => moment(timestamp).tz(this.lineChart.attr(`timezone`)).format(`YYYY-MM-DD`);

    const annotations = await annotationsClient.query({
      projectId: parseInt(getProjectId()),
      fromDate: formatDate(xAxis.minDateMs),
      toDate: formatDate(xAxis.maxDateMs),
      workspaceId: parseInt(getWorkspaceId()),
    });

    // check again if highcharts is still initialized; by the time the annotationsClient query has finished, highcharts may have been destroyed
    if (this.lineChart.getHighcharts()) {
      this.process(annotations);
    }
  }

  locationDetails(annotation: HigchartsAnnotation, timestampMs: number): ButtonLocationDetails {
    return {
      element: annotation.labelsGroup.element,
      formattedDate: this.timestampToTimeUnitFunction()(timestampMs),
      timestampMs,
    };
  }

  popupClosed() {
    this.updateLineChartState({
      viewingAnnotations: false,
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
    });
  }

  /**
   * Group annotations by the nearest chart timestamp that is less than the
   * annotation timestamp, sort them in ascending order, and update the app state
   */
  process(annotationsToProcess: Annotation[]) {
    const utcOffset = this.lineChart.attr(`utc-offset`);
    const projectTimezone = this.lineChart.attr(`timezone`);

    const allAnnotations = this.lineChart.state.annotations.allAnnotations;

    annotationsToProcess.forEach((annotation) => {
      annotation.highchartsDate = offsetForHighcharts(moment(annotation.date), projectTimezone, utcOffset);

      const annotationDateMS = moment.utc(annotation.highchartsDate).valueOf();
      const index = parseInt(this.ticks[sortedLastIndex(this.ticks, annotationDateMS.toString()) - 1]);

      if (allAnnotations.has(index)) {
        const annotationsAtTime = allAnnotations.get(index);
        annotationsAtTime.push(annotation);
        allAnnotations.set(index, annotationsAtTime);
      } else {
        allAnnotations.set(index, [annotation]);
      }
    });

    this.updateLineChartState({allAnnotations});
    this.setAnnotations();
  }

  setAnnotations(minDateMs?: number, maxDateMs?: number) {
    const xAxis = !(minDateMs || maxDateMs) ? this.getXAxisMinAndMax() : {minDateMs, maxDateMs};
    const visibleAnnotations = new Map(this.lineChart.state.annotations.allAnnotations);

    visibleAnnotations.forEach((_, date) => {
      if (date < xAxis.minDateMs || date > xAxis.maxDateMs) {
        visibleAnnotations.delete(date);
      }
    });

    this.updateLineChartState({visibleAnnotations});
    this.redrawGroupButtons();
  }

  redrawCreateButton() {
    if (this.lineChart.attr(`download-mode`)) return;

    this.removePlotLine(HOVER_PLOT_LINE_ID);
    this.removeElements((id) => id === CREATE_ANNOTATION_ID);

    if (
      this.lineChart.attr(`show-annotations`) &&
      this.lineChart.getHoveredTimestampMs() &&
      !this.lineChart.state.annotations.visibleAnnotations.has(this.lineChart.getHoveredTimestampMs())
    ) {
      this.renderCreateAnnotationButton(this.lineChart.getHoveredTimestampMs(), false);
      this.renderPlotLine(this.lineChart.getHoveredTimestampMs(), HOVER_PLOT_LINE_ID);
    }
  }

  redrawGroupButtons() {
    if (this.lineChart.attr(`download-mode`)) return;

    this.removeElements((id) => id.includes(ANNOTATION_GROUP_BUTTON_ID_PREFIX));
    this.removePlotLine(ANNOTATION_PLOT_LINE_ID);

    this.lineChart.state.annotations.visibleAnnotations.forEach((annotations, timestampMsStr) => {
      const timestampMs = Number(timestampMsStr);
      this.renderViewAnnotationsButton(
        timestampMs,
        this.lineChart.state.annotations.activeAnnotationButtonTimestamp === timestampMs,
        annotations.length,
      );
      this.renderPlotLine(timestampMs, ANNOTATION_PLOT_LINE_ID);
    });
  }

  removeElements(matcher = (_) => true) {
    // @ts-ignore Property 'annotations' does not exist on type 'Chart'.
    const annotations: Highcharts.Annotation[] = this.lineChart.getHighcharts()?.annotations;
    if (annotations) {
      [...annotations].forEach((annotation) => {
        if (matcher(annotation.userOptions.id)) {
          this.lineChart.getHighcharts().removeAnnotation(annotation.userOptions.id);
        }
      });
    }
  }

  removePlotLine(plotLineId: string) {
    const xAxis = this.lineChart.getHighcharts().xAxis[0];
    xAxis.removePlotLine(plotLineId);
  }

  renderPlotLine(timestampMs: number, plotLineId: string) {
    const xAxis = this.lineChart.getHighcharts().xAxis[0];
    xAxis.addPlotLine({
      value: timestampMs,
      width: 1,
      id: plotLineId,
    });
  }

  renderCreateAnnotationButton(timestampMs: number, active: boolean) {
    const highchart = this.lineChart.getHighcharts();
    const annotation = highchart.addAnnotation({
      id: CREATE_ANNOTATION_ID,
      crop: false,
      events: {
        // required to prevent default callback from highcharts that inserts a form
        click: () => {
          return;
        },
      },
      labels: [
        {
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          // @ts-ignore Property 'labelOffset' does not exist on type 'Axis'
          distance: -32 - highchart.xAxis[0].labelOffset, // position below x-axis labels
          shape: `rect`,
          padding: 0,
          point: {
            x: timestampMs,
            y: highchart.yAxis[0].getExtremes().min,
            xAxis: 0,
            yAxis: 0,
          },
          useHTML: true,
          verticalAlign: `top`,
          text: `<mp-tooltip placement="top">Add Annotation</mp-tooltip><create-annotation-button active="${active}" />`,
          y: this.lineChart.state.annotations.visibleAnnotations.size ? EXISTING_ANNOTATIONS_BUTTON_Y : 8,
        },
      ],
    });

    annotation.labelsGroup.on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.showCreateAnnotation(detail);
    });

    return annotation;
  }

  renderViewAnnotationsButton(timestampMs: number, active: boolean, numAnnotations: number) {
    const highchart = this.lineChart.getHighcharts();
    const annotation = highchart.addAnnotation({
      crop: false,
      id: `${ANNOTATION_GROUP_BUTTON_ID_PREFIX}-${timestampMs}`,
      events: {
        // required to prevent default callback from highcharts that inserts a form
        click: () => {
          return;
        },
      },
      labels: [
        {
          allowOverlap: true,
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          // @ts-ignore Property 'labelOffset' does not exist on type 'Axis'.
          distance: -32 - highchart.xAxis[0].labelOffset, // position below x-axis labels
          shape: `rect`,
          padding: 0,
          point: {
            x: timestampMs,
            y: highchart.yAxis[0].getExtremes().min,
            xAxis: 0,
            yAxis: 0,
          },
          useHTML: true,
          verticalAlign: `top`,
          text: `<view-annotations-button id="view-annotations-button-${timestampMs}" active="${active}" num-annotations="${numAnnotations}" />`,
          y: EXISTING_ANNOTATIONS_BUTTON_Y,
        },
      ],
    });

    annotation.labelsGroup.on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.viewAnnotationsClicked(detail);
    });

    annotation.labelsGroup.on(`mouseenter`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.updateLineChartState({activeAnnotationPreview: detail});
    });

    annotation.labelsGroup.on(`mouseleave`, () => {
      this.updateLineChartState({activeAnnotationPreview: null});
    });
  }

  timestampToTimeUnitFunction({displayRangeIfWeek = true} = {}) {
    const unit = this.lineChart.attr(`time-unit`);
    const customFormatting = {day: `ddd, MMM D`};
    return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek, customFormatting});
  }

  showCreateAnnotation(activeAnnotationButtonLocationDetails: ButtonLocationDetails) {
    const date = moment.unix(activeAnnotationButtonLocationDetails.timestampMs / 1000);
    this.updateLineChartState({
      creatingAnnotation: true,
      createAnnotationDate: date.format(`MMM D, YYYY HH:mm:ss`),
      activeAnnotationButton: activeAnnotationButtonLocationDetails,
      viewingAnnotations: false,
    });
  }

  updateLineChartState(state: Partial<AnnotationState>) {
    const annotations = Object.assign({}, this.lineChart.state.annotations, state);
    this.lineChart.update({annotations});
  }

  viewAnnotationsClicked(detail: ButtonLocationDetails) {
    this.updateLineChartState({
      creatingAnnotation: false,
      viewingAnnotations: true,
      activeAnnotationButton: detail,
      activeAnnotationPreview: null,
    });

    const activeAnnotationGroup = this.lineChart.state.annotations.visibleAnnotations.get(
      this.lineChart.state.annotations.activeAnnotationButton.timestampMs,
    );

    track(TrackingEvent.Read, {
      [TrackingEventProperty.AnnotationText]: activeAnnotationGroup
        .map((annotation) => annotation.description)
        .join(`, `),
      [TrackingEventProperty.AnnotationTimestamp]: moment(activeAnnotationGroup[0].date).format(`YYYY-MM-DD HH:mm:ss`),
      [TrackingEventProperty.ReportName]: getReportName(),
      [TrackingEventProperty.AnnotationCount]: activeAnnotationGroup.length,
    });
  }
}
